import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { showModal } from "../statusModal/statusModal";
import {
  deleteSquadAPI,
  fetchDashboardSquadsAPI,
  updateSquadAPI,
} from "./squadsAPI";

const initialState = {
  list: [],
  fetchStatus: "idle",
  updateStatus: "idle",
  deleteStatus: "idle",
  currentPage: 1,
  totalPages: 1,
  pageLimit: 10,
  currentQuery: "",
  error: null,
};

export const fetchDashboardSquads = createAsyncThunk(
  "squads/fetchDashboardSquads",
  async ({ page = 1, query = "" }, { dispatch, getState, rejectWithValue }) => {
    try {
      const limit = getState().squads.pageLimit;
      const data = await fetchDashboardSquadsAPI({ query, page, limit });
      return { ...data, query };
    } catch (error) {
      dispatch(
        showModal({ message: "حصل خطأ اثناء جلب السكوادات", status: "error" }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch squads",
      );
    }
  },
);

export const deleteSquad = createAsyncThunk(
  "squads/deleteSquad",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const data = await deleteSquadAPI(id);
      dispatch(
        showModal({
          message: `تم حذف السكواد ${data?.data?.name || ""} بنجاح`,
          status: "success",
        }),
      );
      return data?.data?._id || id;
    } catch (error) {
      dispatch(
        showModal({ message: "حصل خطأ اثناء حذف السكواد", status: "error" }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete squad",
      );
    }
  },
);

export const updateSquad = createAsyncThunk(
  "squads/updateSquad",
  async ({ squadId, squadData }, { dispatch, rejectWithValue }) => {
    try {
      const data = await updateSquadAPI(squadId, squadData);
      dispatch(
        showModal({
          message: "تم تحديث السكواد بنجاح",
          status: "success",
        }),
      );
      return data?.data;
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ اثناء تحديث السكواد",
          status: "error",
        }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to update squad",
      );
    }
  },
);

const squadsSlice = createSlice({
  name: "squads",
  initialState,
  reducers: {
    resetSquadsState: (state) => {
      state.list = [];
      state.fetchStatus = "idle";
      state.updateStatus = "idle";
      state.deleteStatus = "idle";
      state.currentPage = 1;
      state.totalPages = 1;
      state.currentQuery = "";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardSquads.pending, (state) => {
        state.fetchStatus = "loading";
        state.error = null;
      })
      .addCase(fetchDashboardSquads.fulfilled, (state, action) => {
        const page = Number(action.meta.arg?.page || 1);
        const responseData = action.payload?.data || {};
        const fetchedSquads = Array.isArray(responseData.data)
          ? responseData.data
          : [];

        state.fetchStatus = "succeeded";
        state.currentQuery = String(action.payload?.query || "").trim();
        state.currentPage = Number(responseData.page || page);
        state.totalPages = Number(responseData.totalPages || 0);

        if (page === 1) {
          state.list = fetchedSquads;
          return;
        }

        const existingIds = new Set(state.list.map((item) => String(item._id)));
        const deduped = fetchedSquads.filter(
          (item) => !existingIds.has(String(item._id)),
        );
        state.list = [...state.list, ...deduped];
      })
      .addCase(fetchDashboardSquads.rejected, (state, action) => {
        state.fetchStatus = "failed";
        state.error = action.payload || "Failed to fetch squads";
      })

      .addCase(deleteSquad.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null;
      })
      .addCase(deleteSquad.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        state.list = state.list.filter((squad) => squad._id !== action.payload);
      })
      .addCase(deleteSquad.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.error = action.payload || "Failed to delete squad";
      })

      .addCase(updateSquad.pending, (state) => {
        state.updateStatus = "loading";
        state.error = null;
      })
      .addCase(updateSquad.fulfilled, (state, action) => {
        state.updateStatus = "succeeded";
        state.list = state.list.map((squad) => {
          if (squad._id !== action.payload?._id) return squad;
          return {
            ...squad,
            ...(action.payload || {}),
          };
        });
      })
      .addCase(updateSquad.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.error = action.payload || "Failed to update squad";
      });
  },
});

export const { resetSquadsState } = squadsSlice.actions;

export default squadsSlice.reducer;
